/**
 * MOMKIS - Recherche collection (toute la collection, indépendante de la
 * pagination) pour sections/momkis-collection-catalog.liquid.
 *
 * Principe : au premier caractère tapé, récupère en tâche de fond
 * l'intégralité des produits de LA collection courante via l'endpoint
 * réel Shopify /collections/{handle}/products.json (limit=250, pagination
 * de l'endpoint suivie jusqu'à épuisement — jamais un sous-ensemble
 * silencieux), construit un petit index en mémoire (id/handle/titre/prix/
 * image/disponibilité), puis filtre cet index localement à chaque frappe
 * (debounce). L'index est mis en cache par handle de collection : une
 * seconde recherche ne refait aucun appel réseau.
 *
 * Pendant une recherche active (champ non vide), la grille + les filtres
 * natifs Dawn (#main-collection-filters / #ProductGridContainer) sont
 * masqués (jamais détruits) et remplacés par la zone de résultats ; dès
 * que le champ est vidé, la grille/filtres/tri/pagination Dawn natifs
 * réapparaissent strictement tels quels.
 *
 * N'utilise jamais /search (recherche globale hors collection) et ne
 * réécrit aucune logique de filtre/tri/pagination Dawn.
 */
(function () {
  var DEBOUNCE_MS = 300;
  var FETCH_LIMIT = 250; // maximum réel de l'endpoint products.json
  var HARD_PAGE_CEILING = 40; // garde-fou anti-boucle infinie, pas un plafond produit silencieux

  // Cache mémoire par handle de collection : { promise } le temps de la session de page.
  var indexCache = {};

  function normalize(text) {
    return (text || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function formatMoney(cents, format) {
    // products.json renvoie déjà des prix en unité monétaire (ex. "4.67"),
    // pas en centimes : on réutilise directement le format shop.money_format
    // (mêmes patterns que Shopify : amount / amount_no_decimals / etc.).
    var amount = parseFloat(cents);
    if (isNaN(amount)) return '';
    var value = amount.toFixed(2);
    var parts = value.split('.');
    var noDecimals = parts[0];
    var withComma = value.replace('.', ',');
    var noDecimalsWithComma = noDecimals;

    var placeholder = format || '${{amount}}';
    return placeholder
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsWithComma)
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount}}', value);
  }

  function productMinPrice(product) {
    var prices = (product.variants || [])
      .map(function (v) {
        return parseFloat(v.price);
      })
      .filter(function (p) {
        return !isNaN(p);
      });
    if (prices.length === 0) return null;
    return Math.min.apply(null, prices);
  }

  function productAvailable(product) {
    return (product.variants || []).some(function (v) {
      return v.available;
    });
  }

  function productImage(product) {
    if (product.images && product.images[0]) return product.images[0].src;
    var withImage = (product.variants || []).find(function (v) {
      return v.featured_image && v.featured_image.src;
    });
    return withImage ? withImage.featured_image.src : null;
  }

  function buildIndexEntry(product, moneyFormat) {
    var minPrice = productMinPrice(product);
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      url: '/products/' + product.handle,
      image: productImage(product),
      available: productAvailable(product),
      priceLabel: minPrice === null ? '' : formatMoney(minPrice, moneyFormat),
      searchText: normalize(product.title + ' ' + (product.vendor || '') + ' ' + (product.product_type || ''))
    };
  }

  function fetchAllProducts(handle, moneyFormat, signal) {
    var results = [];

    function fetchPage(page) {
      var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + FETCH_LIMIT + '&page=' + page;
      return fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('momkis-collection-search: network error ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          results = results.concat(
            products.map(function (product) {
              return buildIndexEntry(product, moneyFormat);
            })
          );
          if (products.length === FETCH_LIMIT && page < HARD_PAGE_CEILING) {
            return fetchPage(page + 1);
          }
          return results;
        });
    }

    return fetchPage(1);
  }

  function getIndex(handle, moneyFormat) {
    if (!indexCache[handle]) {
      indexCache[handle] = fetchAllProducts(handle, moneyFormat).catch(function (err) {
        delete indexCache[handle]; // permet une nouvelle tentative à la prochaine recherche
        throw err;
      });
    }
    return indexCache[handle];
  }

  function renderResults(root, matches) {
    var grid = root.resultsGrid;
    grid.innerHTML = '';

    matches.forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'grid__item';

      var card = document.createElement('a');
      card.href = item.url;
      card.className = 'momkis-search-card';

      var media = document.createElement('span');
      media.className = 'momkis-search-card__media';
      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.loading = 'lazy';
        img.alt = item.title;
        media.appendChild(img);
      }
      if (!item.available) {
        var badge = document.createElement('span');
        badge.className = 'momkis-search-card__badge';
        badge.textContent = 'Épuisé';
        media.appendChild(badge);
      }
      card.appendChild(media);

      var info = document.createElement('span');
      info.className = 'card-information';

      var heading = document.createElement('span');
      heading.className = 'card__heading momkis-search-card__title';
      heading.textContent = item.title;
      info.appendChild(heading);

      if (item.priceLabel) {
        var price = document.createElement('span');
        price.className = 'momkis-search-card__price';
        price.textContent = item.priceLabel;
        info.appendChild(price);
      }

      card.appendChild(info);
      li.appendChild(card);
      grid.appendChild(li);
    });
  }

  function setStatus(root, message, isError) {
    root.status.textContent = message;
    root.status.classList.toggle('is-error', Boolean(isError));
  }

  function showNativeCatalog(root, show) {
    root.nativeCatalogs.forEach(function (el) {
      el.hidden = !show;
    });
    root.resultsWrap.hidden = show;
  }

  function runSearch(root, query) {
    var trimmed = query.trim();

    if (trimmed === '') {
      root.clearBtn.hidden = true;
      showNativeCatalog(root, true);
      setStatus(root, '');
      setUrlQuery(null);
      return;
    }

    root.clearBtn.hidden = false;
    showNativeCatalog(root, false);
    setUrlQuery(trimmed);

    var isFirstLoad = !indexCache[root.handle];
    if (isFirstLoad) {
      root.spinner.hidden = false;
      setStatus(root, 'Recherche…');
    }

    getIndex(root.handle, root.moneyFormat)
      .then(function (index) {
        root.spinner.hidden = true;
        // La requête a pu changer pendant le chargement de l'index : on ne
        // rend que si le champ contient toujours la même valeur.
        if (normalize(root.input.value.trim()) !== normalize(trimmed)) return;

        var needle = normalize(trimmed);
        var matches = index.filter(function (item) {
          return item.searchText.indexOf(needle) !== -1;
        });

        renderResults(root, matches);

        if (matches.length === 0) {
          setStatus(root, 'Aucun produit ne correspond à votre recherche.');
        } else if (matches.length === 1) {
          setStatus(root, '1 produit trouvé');
        } else {
          setStatus(root, matches.length + ' produits trouvés');
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        root.spinner.hidden = true;
        setStatus(root, 'La recherche est momentanément indisponible. Réessayez.', true);
      });
  }

  function setUrlQuery(value) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (value) {
        url.searchParams.set('q', value);
      } else {
        url.searchParams.delete('q');
      }
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {
      // Environnement sans URL/history exploitable : on ignore silencieusement,
      // la recherche reste fonctionnelle sans persistance d'URL.
    }
  }

  function initRoot(rootEl) {
    var input = rootEl.querySelector('[data-momkis-collection-search]');
    var clearBtn = rootEl.querySelector('[data-momkis-collection-search-clear]');
    var spinner = rootEl.querySelector('[data-momkis-collection-search-spinner]');
    var status = rootEl.querySelector('[data-momkis-collection-search-status]');
    var resultsWrap = document.querySelector('[data-momkis-search-results]');
    var resultsGrid = resultsWrap ? resultsWrap.querySelector('[data-momkis-search-results-grid]') : null;
    var nativeCatalogs = Array.prototype.slice.call(document.querySelectorAll('[data-momkis-native-catalog]'));

    if (!input || !resultsWrap || !resultsGrid) return;

    var root = {
      handle: rootEl.dataset.collectionHandle,
      moneyFormat: rootEl.dataset.moneyFormat,
      input: input,
      clearBtn: clearBtn,
      spinner: spinner,
      status: status,
      resultsWrap: resultsWrap,
      resultsGrid: resultsGrid,
      nativeCatalogs: nativeCatalogs
    };

    var debounceTimer = null;
    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      var value = input.value;
      debounceTimer = setTimeout(function () {
        runSearch(root, value);
      }, DEBOUNCE_MS);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        input.focus();
        runSearch(root, '');
      });
    }

    // Restauration depuis ?q= au chargement (partage/retour arrière), sans
    // toucher aux autres paramètres (filtres/tri Dawn).
    try {
      var initialQuery = new URL(window.location.href).searchParams.get('q');
      if (initialQuery) {
        input.value = initialQuery;
        runSearch(root, initialQuery);
      }
    } catch (e) {
      // ignore
    }
  }

  function init() {
    document.querySelectorAll('[data-momkis-collection-search-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
